import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, createTheme, Tab, Tabs, ThemeProvider, GlobalStyles } from "@mui/material";
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import "../../../components/colors/colors.module.scss";
import { ROUTES } from "../../../utils/constants";
import { LayoutPage } from "../../layoutPage";
import { getAuth } from "../../../redux/auth/authSlice";
import { useSelector } from "react-redux";
import { useSnackbarContext } from "../../../components/globalSnackNotify/globalSnackNotifyContext";
import AutoModeSharpIcon from "@mui/icons-material/AutoModeSharp";
import { MilestoneAccordion, ActionBar, ConfirmationDialogs, MilestoneModal } from "./components";
import { useMilestoneState } from "./hooks/useMilestoneState";
import { useDivisionMileStoneData } from "../../../reactQuery/division/divisionMileStoneQuery";
import { DivisionMileStones } from "../../../api/divisionMileStones/types";
import { divisionSettingsStyles, theme } from "./divisionSettings.styles";
import { useChangeTasks } from "../../../reactQuery/changeTasks/changeTasksQuery";
import { DivisionMileStone } from "../../../api/divisionMileStones/types";
import { createMileStonesBatch } from "../../../api/divisionMileStones/divisionMileStonesApi";
import { useDivisionFeatureFlags } from "../../../stateContext/featureFlags/FeatureFlagProvider";
import { divSettingsState } from "../../../components/decisionDialogBox/decisionDialogBoxContext";
import { useBrowserClosureWarning } from "../../../utils/functions";
import { isDivisionAdminUser, isAppAdminUser } from "../../../utils/permissions";

export const DivisionSettings = () => {
     const navigate = useNavigate();
     const { userInfo } = useSelector(getAuth);
     
     useEffect(() => {
          if (userInfo) {
               const roles = userInfo.userRoles || [];
               if (!isDivisionAdminUser(roles) && !isAppAdminUser(roles)) {
                    navigate(ROUTES.closings.myConcierge.route);
               }
          }
     }, [userInfo, navigate]);

     const { data: divisionMilestones, refetch } = useDivisionMileStoneData(userInfo?.userDivCode);
     const { divisionSettingsAddNewUpdate, divisionSettingsReordering} = useDivisionFeatureFlags(userInfo?.userDivCode || "");




     const [value, setValue] = React.useState(0);
     const { showSnackbar } = useSnackbarContext();
     const [editedMilestones, setEditedMilestones] = useState<DivisionMileStones | null>(null);
     const [showMilestoneModal, setShowMilestoneModal] = useState(false);

     // Memoize the change tasks query parameters to prevent unnecessary re-renders
     const changeTasksParams = React.useMemo(() => ({
          divisionCode: userInfo.userDivCode,
          eventType: 'divisionMileStonesUpdate'
     }), [userInfo.userDivCode]);

     const { data: changeTasks } = useChangeTasks(changeTasksParams.divisionCode, changeTasksParams.eventType);

     // Memoize the milestones to prevent unnecessary re-renders
     // Use editedMilestones if available, otherwise use divisionMilestones from API
     const memoizedMilestones = React.useMemo(() => {
          return editedMilestones || divisionMilestones || { counts: "0", data: [] };
     }, [editedMilestones, divisionMilestones]);

     const handleSaveDraft = async (milestones: DivisionMileStones) => {
          try {
               setEditedMilestones(milestones);
               showSnackbar('Draft saved successfully!', 'success');
          } catch (error) {
               showSnackbar('Failed to save draft', 'error');
          }
     };

     const handleSubmitChanges = async (milestones: DivisionMileStones) => {
          setEditedMilestones(null);
          // Force a fresh fetch to ensure we get the latest released data
          await refetch();
     };

     const handleCancel = () => {
          setEditedMilestones(null);
          showSnackbar('Changes discarded successfully.', 'info');
     };

     const handleNavigationWarning = () => {
          showSnackbar('You have unsaved changes. Please save or discard them before navigating.', 'info');
     };

     const {
          groupedMilestones,
          expandedAccordions,
          hasDraftChanges: internalHasDraftChanges,
          hasUnsavedChanges,
          areAllTasksReleased,
          showSubmitDialog,
          showNavigationDialog,
          handleMilestoneCheckboxChange,
          handleTaskCheckboxChange,
          getTaskCheckboxState,
          getDisplayTasks,
          getTaskChipType,
          getMilestoneChipData,
          areAllTasksChecked,
          areSomeTasksChecked,
          handleAccordionChange,
          handleExpandAll,
          handleCollapseAll,
          handleSaveDraft: internalHandleSaveDraft,
          handleSubmitChanges: internalHandleSubmitChanges,
          handleConfirmSubmit,
          handleCancel: internalHandleCancel,
          handleConfirmNavigation,
          handleCloseDialog,
          handleReorderMilestones,
          handleReorderTasks,
          handleMoveTaskBetweenMilestones,
     } = useMilestoneState({
          milestones: memoizedMilestones,
          setEMilestones: setEditedMilestones,
          initiallyExpanded: false,
          onSaveDraft: handleSaveDraft,
          onSubmitChanges: handleSubmitChanges,
          onCancel: handleCancel,
          onNavigationWarning: handleNavigationWarning,
     });

     const handleSaveDraftWithNotification = useCallback(() => {
          internalHandleSaveDraft();
          showSnackbar('Changes saved as draft successfully!', 'success');
     }, [internalHandleSaveDraft, showSnackbar]);

     const handleSubmitChangesWithNotification = useCallback(() => {
          internalHandleSubmitChanges();
     }, [internalHandleSubmitChanges]);

     const handleConfirmSubmitWithNotification = useCallback(() => {
          handleConfirmSubmit();
          showSnackbar('Changes submitted successfully! Please allow a few minutes for the changes to be reflected.', 'success');
     }, [handleConfirmSubmit, showSnackbar]);

     const handleConfirmNavigationWithNotification = useCallback(() => {
          handleConfirmNavigation();
          showSnackbar('Navigation confirmed.', 'info');
     }, [handleConfirmNavigation, showSnackbar]);

     useEffect(() => {
          divSettingsState.unsaved = hasUnsavedChanges;
          divSettingsState.save = handleSaveDraftWithNotification;
          divSettingsState.cancel = internalHandleCancel;
          /* Reset everything if I decide to leave the page */
          return () => {
               divSettingsState.unsaved = false;
               divSettingsState.save = () => {};
               divSettingsState.cancel = () => {};
          };
     }, [hasUnsavedChanges, handleSaveDraftWithNotification, internalHandleCancel]);

     /* Native browser prompt on tab close / refresh if there are unsaved changes. */
     useBrowserClosureWarning(hasUnsavedChanges);

     // Drag and drop handler
     const handleDragEnd = (result: DropResult) => {
          const { destination, source, type, draggableId } = result;

          // If no destination, user cancelled the drag
          if (!destination) {
               return;
          }

          // If dropped in the same position, do nothing
          if (destination.droppableId === source.droppableId && destination.index === source.index) {
               return;
          }

          if (type === 'milestone') {
               // Reordering milestones
               handleReorderMilestones(source.index, destination.index);
          } else if (type === 'task'){
               // Check if task is being moved between different milestones
               if (source.droppableId !== destination.droppableId) {
                    // Moving task between milestones
                    handleMoveTaskBetweenMilestones(
                         draggableId,
                         source.droppableId,
                         destination.droppableId,
                         destination.index
                    );
               } else {
                    // Reordering tasks within the same milestone
                    const milestoneCode = source.droppableId;
                    handleReorderTasks(milestoneCode, source.index, destination.index);
               }
          }
     };

     const handleAddEditMilestones = () => {
          setShowMilestoneModal(true);
     };

     const handleMilestoneModalSave = async (newMilestones: DivisionMileStone[]) => {
          try {
               console.log('Calling API to save milestones', newMilestones);

               // Call the API to save the new milestones
               const savedMilestones = await createMileStonesBatch(
                    newMilestones,
                    'draft', // mode
                    `workflow_${Date.now()}` // workFlowId
               );

               console.log('API response', savedMilestones);

               // Get existing milestones (from editedMilestones if available, otherwise from divisionMilestones)
               const existingMilestones = memoizedMilestones.data || [];

               // Merge new milestones with existing ones
               // Create a map to track existing milestones by a unique key (mileStoneCode + taskCode)
               const existingMilestonesMap = new Map<string, DivisionMileStone>();
               existingMilestones.forEach(milestone => {
                    const key = `${milestone.mileStoneCode}_${milestone.taskCode}`;
                    existingMilestonesMap.set(key, milestone);
               });

               // Add new milestones, replacing any duplicates
               savedMilestones.forEach(milestone => {
                    const key = `${milestone.mileStoneCode}_${milestone.taskCode}`;
                    existingMilestonesMap.set(key, milestone);
               });

               // Convert map back to array
               const mergedMilestones = Array.from(existingMilestonesMap.values());

               // Update the local milestones with the merged data
               const updatedMilestones = new DivisionMileStones(
                    mergedMilestones.length.toString(),
                    mergedMilestones
               );
               setEditedMilestones(updatedMilestones);

               // Refetch the data to get the latest state
               await refetch();

               showSnackbar('Milestones updated successfully!', 'success');
          } catch (error) {
               console.error('Failed to save milestones', error);
               showSnackbar('Failed to save milestones', 'error');
          }
     };

     const handleMilestoneModalClose = () => {
          setShowMilestoneModal(false);
     };

     // Memoize display tasks for each milestone to avoid recalculation on every render
     const memoizedDisplayTasks = React.useMemo(() => {
          const tasksMap = new Map<string, any[]>();
          groupedMilestones.forEach((milestone) => {
               const displayTasks = getDisplayTasks(milestone);
               tasksMap.set(milestone.mileStoneCode, displayTasks);
          });
          return tasksMap;
     }, [groupedMilestones, getDisplayTasks]);

     // Memoize milestone state calculations
     const milestoneStates = React.useMemo(() => {
          return groupedMilestones.map((milestoneItem) => {
               const displayTasks = memoizedDisplayTasks.get(milestoneItem.mileStoneCode) || [];

               // Check if this specific milestone has any tasks with unsaved changes
               const milestoneHasUnsavedChanges = displayTasks.some(task => {
                    const currentState = getTaskCheckboxState(task.id);
                    return currentState !== undefined && currentState !== task.status;
               });

               // Check if this specific milestone has any tasks being synced
               const milestoneHasSyncProgress = (changeTasks?.counts ? parseInt(changeTasks.counts) : 0) > 0 &&
                    milestoneHasUnsavedChanges;

               return {
                    milestone: milestoneItem,
                    displayTasks,
                    hasMilestoneSyncProgress: milestoneHasSyncProgress,
                    hasMilestoneUnsavedChanges: milestoneHasUnsavedChanges
               };
          });
     }, [groupedMilestones, memoizedDisplayTasks, getTaskCheckboxState, changeTasks?.counts]);

     return (
          <>
          <GlobalStyles
               styles={{
                    // Ensure react-beautiful-dnd dragging elements appear above everything
                    'div[data-rbd-draggable-id]': {
                         zIndex: '10000 !important',
                    },
                    'div[data-rbd-drag-handle-draggable-id]': {
                         zIndex: '10000 !important',
                    },
                    // Ensure toolbar stays visible
                   
                    // Ensure division settings header stays visible
                    '[data-division-header]': {
                         zIndex: '10002 !important',
                    },
                    // Prevent page scrolling on division settings page
                    'html, body': {
                         overflow: 'hidden !important',
                         height: '100% !important',
                    },
                    '.content': {
                         overflow: 'hidden !important',
                         height: 'calc(100vh - 56px) !important',
                         position: 'relative !important',
                         '&::-webkit-scrollbar': { display: 'none' },
                         scrollbarWidth: 'none',
                         msOverflowStyle: 'none',
                    },
                    '.layoutpage-container': {
                         paddingTop: '0 !important',
                         paddingLeft: '0 !important',
                         overflow: 'hidden !important',
                    },
                    '.lennar-container': {
                         height: '100% !important',
                         overflow: 'hidden !important',
                         position: 'relative !important',
                    },
               }}
          />
          <ThemeProvider theme={createTheme({
               ...theme,
               components: {
                    MuiTooltip: {
                         defaultProps: {
                              arrow: true,
                              enterDelay: 500,
                              leaveDelay: 0,
                         },
                         styleOverrides: {
                              tooltip: {
                                   fontSize: '12px',
                              },
                              popper: {
                                   zIndex: '11000 !important',
                              },
                         },
                    },
                    MuiPopover: {
                         styleOverrides: {
                              root: {
                                   zIndex: '11001 !important',
                              },
                         },
                    },
                    MuiDialog: {
                         styleOverrides: {
                              root: {
                                   zIndex: '11002 !important',
                              },
                         },
                    },
                    MuiAccordion: {
                         defaultProps: {
                              TransitionProps: {
                                   unmountOnExit: true,
                              },
                         },
                         styleOverrides: {
                              root: {
                                   overflow: 'visible', // Prevent nested scroll containers for react-beautiful-dnd
                                   '& .MuiAccordionDetails-root': {
                                        overflow: 'visible', // Ensure AccordionDetails doesn't create scroll container
                                   },
                              },
                         },
                    },
                    MuiButton: {
                         defaultProps: {
                              disableRipple: false,
                         },
                    },
               },
          })}>
               <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: 'calc(100vh - 56px)', 
                    overflow: 'hidden',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
               }}>
                    <Box 
                         data-division-header
                         sx={{ 
                         flexShrink: 0,
                         zIndex: 10002,
                         position: 'sticky',
                         top: 0,
                         width: '100%',
                         pt: 0,
                         pb: 1,
                         pl: 0,
                         pr: 4,
                         overflow: 'hidden',
                         '&::-webkit-scrollbar': { display: 'none' },
                         scrollbarWidth: 'none',
                         msOverflowStyle: 'none',
                    }}>
                         <LayoutPage title={ROUTES.settings.division.label}>
                              <></>
                         </LayoutPage>
                    </Box>
                    <div style={{...divisionSettingsStyles.container, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex'}}>
                         <Box sx={divisionSettingsStyles.mainBox}>
                              <Tabs
                                   value={value}
                                   onChange={(event, newValue) => setValue(newValue)}
                                   orientation="vertical"
                                   sx={divisionSettingsStyles.tabs}
                              >
                                   <Tab
                                        label={
                                             <Box sx={divisionSettingsStyles.tabLabel}>
                                                  <AutoModeSharpIcon sx={divisionSettingsStyles.tabIcon} />
                                                  <span>Milestone Settings</span>
                                             </Box>
                                        }
                                        value={0}
                                        sx={divisionSettingsStyles.tab}
                                        disabled
                                   />
                              </Tabs>
                              <Box sx={divisionSettingsStyles.contentBox}>
                                   {value === 0 && (
                                        <Box sx={divisionSettingsStyles.contentContainer}>
                                             <ActionBar
                                                  hasDraftChanges={internalHasDraftChanges}
                                                  hasUnsavedChanges={hasUnsavedChanges}
                                                  groupedMilestonesLength={groupedMilestones.length}
                                                  expandedAccordionsSize={expandedAccordions.size}
                                                  onExpandAll={handleExpandAll}
                                                  onCollapseAll={handleCollapseAll}
                                                  onCancel={internalHandleCancel}
                                                  onSaveDraft={handleSaveDraftWithNotification}
                                                  onSubmitChanges={handleSubmitChangesWithNotification}
                                                  workflowTasksCount={changeTasks?.counts ? parseInt(changeTasks.counts) : 0}
                                                  allTasksReleased={areAllTasksReleased}
                                                  onAddEditMilestones={handleAddEditMilestones}
                                                  divisionSettingsAddNewUpdate={divisionSettingsAddNewUpdate}

                                             />

                                             <DragDropContext onDragEnd={handleDragEnd}>
                                                  <Droppable droppableId="milestones" type="milestone">
                                                       {(provided, snapshot) => (
                                                            <Box
                                                                 ref={provided.innerRef}
                                                                 {...provided.droppableProps}
                                                                 sx={{
                                                                      ...divisionSettingsStyles.milestonesContainer,
                                                                      backgroundColor: snapshot.isDraggingOver ? '#f5f5f5' : 'transparent',
                                                                      borderRadius: 1,
                                                                      padding: snapshot.isDraggingOver ? 1 : 0,
                                                                      transition: snapshot.isDraggingOver ? 'none' : 'all 0.2s ease',
                                                                      minHeight: snapshot.isDraggingOver ? 40 : 0,
                                                                 }}
                                                            >
                                                                 {groupedMilestones.map((milestone, index) => {
                                                                      const milestoneState = milestoneStates.find(ms => ms.milestone.mileStoneCode === milestone.mileStoneCode);
                                                                      const {
                                                                           hasMilestoneSyncProgress = false,
                                                                           hasMilestoneUnsavedChanges = false
                                                                      } = milestoneState || {};
                                                                      return (
                                                                           <Draggable
                                                                                key={milestone.mileStoneCode}
                                                                                draggableId={milestone.mileStoneCode}
                                                                                index={index}
                                                                           >
                                                                                {(provided, snapshot) => (
                                                                                     <Box
                                                                                          ref={provided.innerRef}
                                                                                          {...provided.draggableProps}
                                                                                          sx={{
                                                                                               marginBottom: 2,
                                                                                               transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                                                                                          }}
                                                                                          style={{
                                                                                               ...provided.draggableProps.style,
                                                                                               zIndex: snapshot.isDragging ? '10000' : undefined,
                                                                                          }}
                                                                                     >
                                                                                               <MilestoneAccordion
                                                                                               milestone={milestone}
                                                                                               index={index}
                                                                                               dragHandleProps={provided.dragHandleProps}
                                                                                               isExpanded={expandedAccordions.has(milestone.mileStoneCode)}
                                                                                               onAccordionChange={handleAccordionChange}
                                                                                               onMilestoneCheckboxChange={handleMilestoneCheckboxChange}
                                                                                               onTaskCheckboxChange={handleTaskCheckboxChange}
                                                                                               getTaskCheckboxState={getTaskCheckboxState}
                                                                                               getDisplayTasks={getDisplayTasks}
                                                                                               getTaskChipType={getTaskChipType}
                                                                                               getMilestoneChipData={getMilestoneChipData}
                                                                                               areAllTasksChecked={areAllTasksChecked}
                                                                                               areSomeTasksChecked={areSomeTasksChecked}
                                                                                               hasDraftChanges={internalHasDraftChanges}
                                                                                               hasUnsavedChanges={hasUnsavedChanges}
                                                                                               workflowTasksCount={changeTasks?.counts ? parseInt(changeTasks.counts) : 0}
                                                                                               allTasksReleased={areAllTasksReleased}
                                                                                               hasMilestoneSyncProgress={hasMilestoneSyncProgress}
                                                                                               hasMilestoneUnsavedChanges={hasMilestoneUnsavedChanges}
                                                                                               onReorderTasks={handleReorderTasks}
                                                                                               divisionSettingsReordering={divisionSettingsReordering}
                                                                                          />
                                                                                     </Box>
                                                                                )}
                                                                           </Draggable>
                                                                      );
                                                                 })}
                                                                 {provided.placeholder}
                                                            </Box>
                                                       )}
                                                  </Droppable>
                                             </DragDropContext>

                                             <ConfirmationDialogs
                                                  showSubmitDialog={showSubmitDialog}
                                                  showNavigationDialog={showNavigationDialog}
                                                  onCloseDialog={handleCloseDialog}
                                                  onConfirmSubmit={handleConfirmSubmitWithNotification}
                                                  onConfirmNavigation={handleConfirmNavigationWithNotification}
                                             />

                                             <MilestoneModal
                                                  open={showMilestoneModal}
                                                  onClose={handleMilestoneModalClose}
                                                  onSave={handleMilestoneModalSave}
                                                  availableMilestones={[]}
                                                  availableTasks={[]}
                                                  currentMilestones={memoizedMilestones.data || []}
                                                  divisionCode={userInfo.userDivCode}
                                                  mode="draft"
                                                  onReorderTasks={handleReorderTasks}
                                             />
                                        </Box>
                                   )}
                              </Box>
                         </Box>
                    </div>
               </Box>
          </ThemeProvider>
          </>
     );
};
